import { z } from 'zod';
/**
 * What a studio request is FOR, and the choices that constrain it.
 *
 * Until now the studio asked "what file do you want back" (text, image,
 * audio, video). The real question is the end product: a post, a prompt that
 * will drive an image model, a video script that will drive a video model.
 * The goal decides the instructions the writing model receives, which
 * connector is used at each step, and what the project produces.
 */
/** The end product the user wants. */
export const GenerationGoal = {
    /** A social post, written ready to publish. */
    POST: 'POST',
    /** A prompt for an image-generation model. */
    IMAGE_PROMPT: 'IMAGE_PROMPT',
    /** Script + video prompt from text alone (attachments serve as logo or background knowledge). */
    VIDEO_FROM_TEXT: 'VIDEO_FROM_TEXT',
    /** Script + video prompt that animates the attached image(s). */
    VIDEO_FROM_IMAGE: 'VIDEO_FROM_IMAGE',
    /** Text to be spoken by a text-to-speech model, written to be read aloud. */
    AUDIO_TEXT: 'AUDIO_TEXT',
};
export const GENERATION_GOALS = Object.values(GenerationGoal);
/** For a POST: a picture or video made to go with it, planned in the same request. */
export const GenerationFollowUp = {
    NONE: 'NONE',
    IMAGE: 'IMAGE',
    VIDEO_FROM_TEXT: 'VIDEO_FROM_TEXT',
    VIDEO_FROM_IMAGE: 'VIDEO_FROM_IMAGE',
};
export const GENERATION_FOLLOW_UPS = Object.values(GenerationFollowUp);
/**
 * What a text (SCRIPT) job is for. Text models differ: one writes good posts,
 * another good video prompts. A connector declares the purposes it suits, and
 * a job asks for one.
 */
export const GenerationPurpose = {
    POST_TEXT: 'POST_TEXT',
    IMAGE_PROMPT: 'IMAGE_PROMPT',
    VIDEO_SCRIPT: 'VIDEO_SCRIPT',
    AUDIO_TEXT: 'AUDIO_TEXT',
};
export const GENERATION_PURPOSES = Object.values(GenerationPurpose);
/** Which text purpose a goal needs (the first step of every project is a text job). */
export const PURPOSE_FOR_GOAL = {
    POST: 'POST_TEXT',
    IMAGE_PROMPT: 'IMAGE_PROMPT',
    VIDEO_FROM_TEXT: 'VIDEO_SCRIPT',
    VIDEO_FROM_IMAGE: 'VIDEO_SCRIPT',
    AUDIO_TEXT: 'AUDIO_TEXT',
};
/** What a video connector can take as input. */
export const GenerationVideoInput = { TEXT: 'TEXT', IMAGE: 'IMAGE' };
export const GenerationQualityTier = { DRAFT: 'DRAFT', STANDARD: 'STANDARD', HIGH: 'HIGH' };
export const GENERATION_ASPECTS = ['9:16', '16:9', '1:1', '4:5', '3:4', '4:3'];
/* -------------------------------------------------------------------------- */
/*  Options                                                                   */
/* -------------------------------------------------------------------------- */
export const GENERATION_DIALECTS = [
    'MSA',
    'SAUDI_NAJDI',
    'SAUDI_HIJAZI',
    'GULF',
    'EGYPTIAN',
    'LEVANTINE',
    'MAGHREBI',
];
/** What a reference is for. The writing model is told, and the media model gets it as the right kind of reference. */
export const GENERATION_ATTACHMENT_ROLES = [
    'LOGO',
    'CHARACTER',
    'PRODUCT',
    'FIRST_FRAME',
    'LAST_FRAME',
    'STYLE',
    'KNOWLEDGE',
    'REFERENCE',
];
const attachmentSchema = z
    .object({
    /** A file already on the account… */
    mediaId: z.string().uuid().optional(),
    /** …or a public https link. Exactly one of the two. */
    url: z.string().url().max(2000).optional(),
    role: z.enum(GENERATION_ATTACHMENT_ROLES).default('REFERENCE'),
})
    .refine((a) => Boolean(a.mediaId) !== Boolean(a.url), {
    message: 'Give either mediaId or url, not both',
});
export const generationOptionsSchema = z.object({
    /** The language of the deliverable. */
    language: z.enum(['ar', 'en']).optional(),
    dialect: z.enum(GENERATION_DIALECTS).optional(),
    /** Arabic diacritics (tashkeel) on text that will be SPOKEN (video dialogue, audio). */
    diacritics: z.enum(['NONE', 'PARTIAL', 'FULL']).optional(),
    /** Free instructions appended to the request. */
    extraInstructions: z.string().max(4000).optional(),
    attachments: z.array(attachmentSchema).max(20).optional(),
    /** Text that must appear ON an image or video. */
    textOnMedia: z
        .object({
        mode: z.enum(['NONE', 'EXACT', 'TITLE']),
        /** For EXACT: the words, to be rendered verbatim. */
        text: z.string().max(500).optional(),
    })
        .optional(),
    post: z
        .object({
        length: z.enum(['SHORT', 'MEDIUM', 'LONG']).optional(),
        tone: z.enum(['FORMAL', 'FRIENDLY', 'EXCITED', 'PERSUASIVE', 'INFORMATIVE']).optional(),
        hashtags: z.boolean().optional(),
        emojis: z.boolean().optional(),
        callToAction: z.boolean().optional(),
    })
        .optional(),
    image: z
        .object({
        aspect: z.enum(GENERATION_ASPECTS).optional(),
        quality: z.enum(['DRAFT', 'STANDARD', 'HIGH']).optional(),
        style: z.string().max(300).optional(),
    })
        .optional(),
    video: z
        .object({
        aspect: z.enum(GENERATION_ASPECTS).optional(),
        quality: z.enum(['DRAFT', 'STANDARD', 'HIGH']).optional(),
        durationSeconds: z.number().int().min(2).max(180).optional(),
        /** Aim for this many scenes. Unset: the writer decides. */
        scenes: z.number().int().min(1).max(30).optional(),
        /** Describe each character once and repeat that description in every scene. */
        characterConsistency: z.boolean().optional(),
        /** Each scene continues where the previous one ended. */
        sceneContinuity: z.boolean().optional(),
        transitions: z.enum(['AUTO', 'CUT', 'FADE', 'DISSOLVE']).optional(),
        pace: z.enum(['SLOW', 'NORMAL', 'FAST']).optional(),
        /** Spoken lines and on-screen text must follow the given order scene by scene. */
        keepScriptOrder: z.boolean().optional(),
        sound: z.enum(['NONE', 'AMBIENT', 'MUSIC', 'AMBIENT_MUSIC']).optional(),
    })
        .optional(),
    audio: z
        .object({
        gender: z.enum(['ANY', 'MALE', 'FEMALE']).optional(),
        /** The provider's own voice id; overrides gender. */
        voice: z.string().max(200).optional(),
        /** 1 = normal. */
        speed: z.number().min(0.5).max(2).optional(),
        tone: z.enum(['NEUTRAL', 'WARM', 'ENERGETIC', 'CALM', 'SERIOUS']).optional(),
    })
        .optional(),
});
/** Saved option sets ("Saudi reels, male voice") reused with one click. */
export const saveOptionPresetSchema = z.object({
    name: z.string().trim().min(1).max(100),
    goal: z.enum(GENERATION_GOALS),
    followUp: z.enum(GENERATION_FOLLOW_UPS).default('NONE'),
    options: generationOptionsSchema,
});
/** Ask which connectors a request would use, before creating anything. */
export const previewRouteSchema = z.object({
    goal: z.enum(GENERATION_GOALS),
    followUp: z.enum(GENERATION_FOLLOW_UPS).default('NONE'),
    options: generationOptionsSchema.default({}),
});
/** The documented defaults, applied by both the server and the form. */
export function defaultGenerationOptions(goal) {
    const common = { language: 'ar', dialect: 'SAUDI_NAJDI' };
    switch (goal) {
        case 'POST':
            return {
                ...common,
                post: { length: 'MEDIUM', tone: 'FRIENDLY', hashtags: true, emojis: true, callToAction: true },
            };
        case 'IMAGE_PROMPT':
            return {
                ...common,
                textOnMedia: { mode: 'NONE' },
                image: { aspect: '1:1', quality: 'STANDARD' },
            };
        case 'VIDEO_FROM_TEXT':
        case 'VIDEO_FROM_IMAGE':
            return {
                ...common,
                diacritics: 'FULL',
                textOnMedia: { mode: 'NONE' },
                video: {
                    aspect: '9:16',
                    quality: 'STANDARD',
                    durationSeconds: 15,
                    characterConsistency: true,
                    sceneContinuity: true,
                    transitions: 'AUTO',
                    pace: 'NORMAL',
                    keepScriptOrder: true,
                    sound: 'AMBIENT_MUSIC',
                },
            };
        case 'AUDIO_TEXT':
            return { ...common, diacritics: 'FULL', audio: { gender: 'ANY', speed: 1, tone: 'NEUTRAL' } };
    }
}
/* -------------------------------------------------------------------------- */
/*  Output size                                                               */
/* -------------------------------------------------------------------------- */
/** What a quality tier means to a video provider that names a resolution. */
export const RESOLUTION_FOR_QUALITY = {
    DRAFT: '480P',
    STANDARD: '720P',
    HIGH: '1080P',
};
/**
 * Pixel sizes for about one megapixel, per aspect ratio: the buckets image
 * models are trained on. DRAFT is half of each side, HIGH is double.
 */
const STANDARD_SIZE = {
    '1:1': [1024, 1024],
    '16:9': [1344, 768],
    '9:16': [768, 1344],
    '4:5': [896, 1120],
    '3:4': [896, 1152],
    '4:3': [1152, 896],
};
const SIZE_SCALE = { DRAFT: 0.5, STANDARD: 1, HIGH: 2 };
/** `WIDTHxHEIGHT` for an aspect ratio at a quality tier, e.g. 1:1 STANDARD → `1024x1024`. */
export function sizeForAspect(aspect, quality = 'STANDARD') {
    const [width, height] = STANDARD_SIZE[aspect];
    const scale = SIZE_SCALE[quality];
    return `${Math.round(width * scale)}x${Math.round(height * scale)}`;
}
//# sourceMappingURL=generationGoals.js.map